import * as Api from '@ViewModels';
import { StyleDeclarationValue, css } from 'aphrodite';
import equal from 'fast-deep-equal/react';
import * as React from 'react';
import { toast } from 'sonner';
import { useErrorMessages } from '../../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../../models/Logging';
import { useDeleteSavedSearchMutation, useSavedSearchesQuery } from '../../../../../queries/ContactSearch';
import { ContactsGutterTag } from '../../../../components/contacts/ContactsGutterTag';
import { SearchIcon } from '../../../../components/svgs/icons/SearchIcon';
import { brandPrimaryHover, white } from '../../../../styles/colors';
import { PeopleCollapsibleSection } from '../PeopleCollapsibleSection';
import { styleSheet } from './styles';

interface IProps {
	className?: string;
	styles?: StyleDeclarationValue[];
	filter: Api.IBulkContactsRequest;
	onChangeFilter: (filter: Api.IBulkContactsRequest) => void;
	impersonationContext?: Api.IImpersonationContext;
}

export const SavedSearches: React.FC<IProps> = ({
	className,
	styles,
	filter,
	onChangeFilter,
	impersonationContext,
}) => {
	const { pushApiError } = useErrorMessages();
	const { logApiError } = useEventLogging();

	// Use the new query hook with meta for error handling
	const { data: searches = [], isLoading } = useSavedSearchesQuery({
		meta: {
			toastErrorMessage: 'Error fetching saved searches',
			errorLogging: {
				category: 'SavedSearches',
				action: 'PeopleSavedSearchesLoadSavedSearches-Error',
			},
		},
	});

	// Use the delete mutation hook
	const deleteMutation = useDeleteSavedSearchMutation({
		onSuccess: () => {
			toast.success('Search was deleted');
		},
		onError: (err: unknown) => {
			const apiError = err as Api.IOperationResultNoValue;
			pushApiError(apiError);
			logApiError('DeleteSavedSearch-Error', apiError);
		},
	});

	const onDeleteSearch = React.useCallback(
		(search: Api.ContactSearch) => () => {
			deleteMutation.mutate({ searchId: search.id, impersonationContext });
		},
		[deleteMutation, impersonationContext]
	);

	const onSelectSaveSearch = React.useCallback(
		(search: Api.ContactSearch) => () => {
			onChangeFilter({ filter: search.filter, ownershipFilter: search.ownershipFilter });
		},
		[onChangeFilter]
	);

	const isMatch = React.useCallback(
		(testFilter: Api.IContactFilterCriteria) => {
			return equal(filter.filter, testFilter);
		},
		[filter]
	);

	if (!searches.length && !isLoading) {
		return (
			<div className={`${css(styleSheet.container, ...(styles || []))} ${className || ''}`}>
				<div className={css(styleSheet.emptyText)}>0 saved searches</div>
			</div>
		);
	}

	const renderSearches = () => {
		return searches.map(search => {
			return (
				<ContactsGutterTag
					className={css(styleSheet.search)}
					key={`peopletable-savedsearches-tag-${search.id}`}
					isActive={isMatch(search.filter)}
					leftAccessory={
						<SearchIcon
							fillColor={isMatch(search.filter) ? white : brandPrimaryHover}
							viewBox='-1 -1 18 18'
							strokeWidth={0.5}
							className={css(styleSheet.searchIcon)}
						/>
					}
					onRemoveButtonClicked={onDeleteSearch(search)}
					showRemoveButton
					updateSelectedTagList={!isMatch(search.filter) ? onSelectSaveSearch(search) : Api.VmUtils.Noop}
					tag={search.name}
					hideCheckbox={true}
				/>
			);
		});
	};

	return (
		<div className={`${css(styleSheet.container, ...(styles || []))} ${className || ''}`}>
			<PeopleCollapsibleSection itemHeightPx={34} items={renderSearches()} showLoader={isLoading} />
		</div>
	);
};
